<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useScenarioStore } from '@/stores/scenario'
import { storeToRefs } from 'pinia'

const props = defineProps({
  activeFiltersCount: {
    type: Number,
    default: 0
  },
  iconColor: {
    type: String,
    default: 'primary'
  },
  activeIcon: {
    type: String,
    default: 'mdi-filter-check'
  },
  inactiveIcon: {
    type: String,
    default: 'mdi-filter-check-outline'
  },
  activeText: {
    type: String,
    default: 'active filters'
  },
  inactiveText: {
    type: String,
    default: 'Select your filters'
  },
  maxWidth: {
    type: [String, Number],
    default: 300
  },
  currentScenarioId: {
    type: Number,
    required: true
  },
  currentProjectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['update-viewing-option', 'update-selected-calculations'])

const viewingOption = ref('one')
const selectedCalculations = ref<number>()

const scenarioStore = useScenarioStore()
const { items: calculations } = storeToRefs(scenarioStore)

const menuOpen = ref(false)

const fetchCalculations = async () => {
  await scenarioStore.fetchItems()
}

watch(menuOpen, async (isOpen) => {
  if (isOpen) {
    await fetchCalculations()
  }
})

const completedCalculations = computed(() =>
  calculations.value.filter(
    (calc) =>
      calc.status === 'Done' &&
      calc.id !== props.currentScenarioId &&
      calc.project === props.currentProjectId
  )
)

watch(selectedCalculations, (newSelection) => {
  emit('update-selected-calculations', newSelection)
})
watch(viewingOption, (newOption) => {
  emit('update-viewing-option', newOption)
  if (newOption === 'one') {
    selectedCalculations.value = undefined
  }
})

onMounted(async () => {
  await scenarioStore.fetchItems()
  await fetchCalculations()
})
</script>

<template>
  <v-menu offset-y :close-on-content-click="false" v-model="menuOpen">
    <template #activator="{ props: activatorProps, isActive }">
      <v-text-field
        v-bind="activatorProps"
        class="font-weight-bold mx-2"
        :style="'max-width: ' + maxWidth + 'px;'"
        variant="solo"
        flat
        hide-details
        readonly
      >
        <template #prepend-inner>
          <v-icon
            class="mx-1"
            :color="iconColor"
            :icon="activeFiltersCount > 0 ? activeIcon : inactiveIcon"
          />
        </template>

        <template #default>
          <span>
            {{ activeFiltersCount > 0 ? activeFiltersCount + ' ' + activeText : inactiveText }}
          </span>
        </template>

        <template #append-inner>
          <v-icon class="mx-1" :icon="isActive ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
        </template>
      </v-text-field>
    </template>

    <v-card>
      <v-card-text class="pa-2">
        <v-radio-group v-model="viewingOption" row dense>
          <v-radio label="One simulation" value="one" color="primary" />
          <v-radio
            label="Two simulations"
            value="two"
            color="primary"
            style="margin-bottom: -20px"
          />
        </v-radio-group>
        <div v-if="viewingOption === 'two'">
          <v-select
            v-model="selectedCalculations"
            :items="completedCalculations"
            item-title="name"
            item-value="id"
            color="primary text-none"
            label="Select Completed Simulations"
            dense
            :menu-props="{ maxHeight: '220px' }"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>
